/**
 * One line above every page when the plan needs attention: the last week of the trial, a payment
 * Stripe could not collect, or a business that has become read-only. Nothing otherwise.
 */
import { TriangleAlert } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { useWorkspace } from "@/lib/session/workspace-context";
import { cn } from "@/lib/utils/cn";

/** The trial is mentioned only in its last week; before that it would be noise. */
const TRIAL_NOTICE_DAYS = 7;

export function PlanBanner() {
  const { t } = useTranslation();
  const { plan, role } = useWorkspace();
  if (!plan) return null;

  let text: string | null = null;
  if (plan.kind === "expired") text = t("planBanner.readOnly");
  else if (plan.kind === "trial" && plan.daysLeft <= TRIAL_NOTICE_DAYS)
    text = t("planBanner.trial", { count: plan.daysLeft });
  else if (plan.kind === "paid" && plan.status === "past_due") text = t("planBanner.pastDue");
  if (!text) return null;

  const urgent = plan.kind !== "trial";
  return (
    <div
      role="status"
      className={cn(
        "mb-6 flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg border px-4 py-3 text-sm",
        urgent ? "border-warning/40 bg-warning/10" : "bg-card",
      )}
      data-testid="plan-banner"
    >
      <TriangleAlert className={cn("size-4 shrink-0", urgent && "text-warning")} />
      <span className="min-w-0 flex-1">{text}</span>
      {role === "owner" ? (
        <Link
          to="/settings?tab=plan"
          className="font-medium text-primary underline decoration-primary/30 underline-offset-2 hover:decoration-primary"
        >
          {plan.kind === "paid" ? t("planBanner.fixPayment") : t("planBanner.choose")}
        </Link>
      ) : (
        <span className="text-muted-foreground">{t("planBanner.askOwner")}</span>
      )}
    </div>
  );
}
